'use client';

import { forwardRef, useId } from 'react';
// eslint-disable-next-line import/no-namespace
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

import {
  checkedClasses,
  defaultClasses,
  disabledClasses,
  focusedClasses,
  hoverClasses,
  indicatorClasses,
  labelClasses,
  labelColorClasses,
  labelDisabledClasses,
  wrapperClasses,
  wrapperHadLabelClasses
} from './Checkbox.classes';
import type { CheckboxElementProps, CheckboxProps, SubLabelProps } from './Checkbox.types';

import { cn } from 'helpers/classnames';

const CheckboxElement = forwardRef<React.ElementRef<CheckboxElementProps>, React.ComponentPropsWithoutRef<CheckboxElementProps>>(
  ({ className, disabled, ...props }, ref) => (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(defaultClasses, focusedClasses, disabled ? disabledClasses : cn(hoverClasses, checkedClasses), className)}
      disabled={disabled}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        <svg width={10} height={8} viewBox="0 0 10 8" fill="none" className={indicatorClasses}>
          <path
            d="M1.21037 4.39384L3.71037 6.68551L8.58786 1.40422"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
);

const Root = forwardRef<React.ElementRef<typeof CheckboxElement>, CheckboxProps>((props, ref) => {
  const { className, label, labelClassName, children, ...forwardProps } = props;
  const { disabled } = props;

  const generatedId = useId();
  const checkboxProps = { ...forwardProps, id: generatedId, ref };

  if (label) {
    return (
      <div className={cn(wrapperClasses, wrapperHadLabelClasses, className)}>
        <CheckboxElement {...checkboxProps} />
        <label className={cn(labelClasses, disabled ? labelDisabledClasses : labelColorClasses, labelClassName)} htmlFor={generatedId}>
          <span>{label}</span>
          {children}
        </label>
      </div>
    );
  }

  return <CheckboxElement {...checkboxProps} className={className} />;
});

const SubLabel = ({ children, className, ...props }: SubLabelProps) => (
  <span className={className} {...props}>
    {children}
  </span>
);

Root.displayName = 'Checkbox';
SubLabel.displayName = 'Checkbox.SubLabel';

export { Root as Checkbox, SubLabel as CheckboxSubLabel };
